/**
 * Shared admin UI primitives.
 *
 * Why these exist:
 * - Avoid duplicated card/KPI styling across admin pages (9 files had it inline)
 * - Enforce consistent theme handling — the `bg-[color:var(--color-surface)]` casing bug that
 *   caused invisible text in light mode is now impossible to reproduce because
 *   every admin card goes through this component.
 * - Prepare Phase B/C refactoring of the rest of the admin area.
 */
import { Link } from 'react-router-dom';

const GOLD = 'var(--color-primary)';

/**
 * Admin card: surface with border/padding that adapts correctly to dark & light themes.
 * Use for any dashboard panel.
 */
export const AdminCard = ({
  className = '',
  children,
  as: Tag = 'div',
  highlight = false,
  ...rest
}) => (
  <Tag
    className={`bg-[color:var(--color-surface)] border ${
      highlight ? 'border-[color:rgb(var(--primary-rgb)/0.30)]' : 'border-white/10'
    } ${className}`}
    {...rest}
  >
    {children}
  </Tag>
);

/**
 * Admin KPI tile: label + value + optional icon & sub-label.
 * Mirrors the pattern used in AdminDashboard and AdminAnalytics.
 */
export const AdminKPI = ({
  label,
  value,
  sub,
  icon: Icon,
  iconColor = GOLD,
  rightSlot = null,
  testid,
  to = null,
}) => {
  const inner = (
    <>
      <div className="flex items-center justify-between mb-3">
        {Icon ? <Icon className="w-6 h-6" style={{ color: iconColor }} /> : <span />}
        {rightSlot}
      </div>
      {label && (
        <p className="text-zinc-500 text-xs uppercase tracking-wider">{label}</p>
      )}
      <p
        className="text-xl text-white font-light"
        style={{ fontFamily: 'Cormorant Garamond, serif' }}
      >
        {value}
      </p>
      {sub && <p className="text-zinc-500 text-xs mt-1">{sub}</p>}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testid}
        className="block bg-[color:var(--color-surface)] border border-white/10 hover:border-[color:rgb(var(--primary-rgb)/0.40)] hover:bg-white/[0.02] transition-colors p-5 cursor-pointer"
      >
        {inner}
      </Link>
    );
  }
  return (
    <AdminCard className="p-5" data-testid={testid}>
      {inner}
    </AdminCard>
  );
};

/**
 * Page header with optional back link and subtitle.
 */
export const AdminPageHeader = ({
  kicker,
  title,
  backTo = null,
  backLabel = 'Retour',
  right = null,
}) => (
  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
    <div>
      {backTo && (
        <Link
          to={backTo}
          className="text-zinc-500 hover:text-[color:var(--color-primary)] text-xs flex items-center gap-1 mb-2"
        >
          ← {backLabel}
        </Link>
      )}
      {kicker && (
        <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-[0.3em] mb-2">
          {kicker}
        </p>
      )}
      <h1
        className="text-3xl text-white font-light"
        style={{ fontFamily: 'Cormorant Garamond, serif' }}
      >
        {title}
      </h1>
    </div>
    {right && <div className="flex gap-3 flex-wrap">{right}</div>}
  </div>
);

/**
 * Quick action link card — used for the admin dashboard's navigation grid.
 */
export const AdminQuickLink = ({
  to,
  icon: Icon,
  title,
  hint,
  badge = null,
  testid,
}) => (
  <Link
    to={to}
    className="bg-[color:var(--color-surface)] border border-white/10 p-4 hover:border-[color:rgb(var(--primary-rgb)/0.30)] transition-colors group relative"
    data-testid={testid}
  >
    {Icon && <Icon className="w-5 h-5 text-[color:var(--color-primary)] mb-2" />}
    <p className="text-white text-sm group-hover:text-[color:var(--color-primary)] transition-colors">
      {title}
    </p>
    {hint && <p className="text-zinc-600 text-xs">{hint}</p>}
    {badge !== null && badge !== undefined && (
      <span className="absolute top-2 right-2 w-5 h-5 bg-orange-500 text-white text-xs flex items-center justify-center rounded-full">
        {badge}
      </span>
    )}
  </Link>
);

/**
 * Loading skeleton: grid of empty cards.
 */
export const AdminSkeletonGrid = ({ count = 4 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
    {Array.from({ length: count }, (_, i) => (
      <AdminCard key={`skel-${i}`} className="p-5 animate-pulse">
        <div className="h-16 bg-white/5" />
      </AdminCard>
    ))}
  </div>
);

/**
 * Centered empty state text.
 */
export const AdminEmpty = ({ children }) => (
  <p className="text-zinc-500 text-center py-6 text-sm">{children}</p>
);
